import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Groupper, Header, Message, Table, Icon, Input, Label, Field, Calendar, Dropdown, DropdownItemProps, MenuItemProps, Menu } from 'react-frontier2';
import { Dropzone } from '@arema/components';
import { addCommas, bindClick, getNow, roundNumber, toCents } from '@arema/components/Util';
import { CardType, ConciliaDeposit, ConciliaStatus, DepositType, ExternalSearchOrder, InvalidParsedOrder, ParsedOrder, SourceBusiness } from '../../AdminClasses';
import { PaymentMethod, PaymentMethods, SetLoading } from '@arema/components/Classes';
import { useTitle } from '@arema/components/Hooks';
import { Link } from 'react-router-dom'
import { firstBy } from 'thenby';
import { ConciliaFile, ConciliaFileType, depositTypeLocale, depositTypeMethod, FileTypeList, getPaymentSource, parseConcilia, readFile } from './ConciliaUtils';
import { useFrontier, Modal } from 'react-frontier2';
import classNames from 'classnames';
import moment from 'moment';
import API from '../../API';
import Validator from '@arema/components/Validator';

enum ShownModal{
	FILE_TYPE = 1,
	FORCE_CONCILIA = 2,
	SINGLE_CONCILIA = 3,
}

const BusinessSources : {
	business_id: SourceBusiness,
	business_name: string,
}[] = [
	{ business_id: SourceBusiness.AREMA, business_name: 'AREMA' },
	{ business_id: SourceBusiness.TICKETLOCK, business_name: 'TicketLock' },
]

var Conciliacion = ()=>{
	var { setTitle } = useTitle();
	var { Toast } = useFrontier();
	var [conciliaText, setConciliaText] = useState<{ filename: string, text: string }[]>(null);
	var [fileType, setFileType] = useState<ConciliaFileType>(null);
	var [conciliaError, setConciliaError] = useState<string>(null);
	var [conciliaOrders, setConciliaOrders] = useState<(ConciliaDeposit & { completed?: boolean })[]>(null);
	var [invalidOrders, setInvalidOrders] = useState<InvalidParsedOrder[]>(null);
	var [depositDate, setDepositDate] = useState<number>(null);
	var [sending, setSending] = useState<boolean>(false);
	var [shownModal, setShownModal] = useState<ShownModal>(null);
	var [conciliaMode, setConciliaMode] = useState<number>(null);
	var [errorPrompts, setErrorPrompts] = useState<string[]>(null);
	var [selectedExternal, setSelectedExternal] = useState<string>(null);
	var [typeSearch, setTypeSearch] = useState<string>('');
	var [searchTimeout, setSearchTimeout] = useState<any>(null);
	var [externalSearchValue, setExternallSearchValue] = useState<string>('');
	var [externalSearch, setExternallSearch] = useState<{ loading: boolean, value: string, error: boolean, order: ExternalSearchOrder }>(null);
	var [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>(null);
	var [paymentSource, setPaymentSource] = useState<number>(null);
	var [modalPaymentSource, setModalPaymentSource] = useState<number>(null);
	var typeSearchRef = useRef<HTMLInputElement>(null);

	useEffect(()=>{
		setTitle('Conciliación');
	}, []);

	useEffect(()=>{
		if(!!conciliaText && conciliaText.length>0 && !fileType){
			if(typeSearchRef){
				setTypeSearch('');
				typeSearchRef.current?.focus()
			}
		}
	}, [conciliaText, fileType]);

	var now = useMemo(()=>{
		return getNow();
	}, []);

	var parseOrders = async (type: ConciliaFileType, date: number)=>{
		if(type.askDate && !date) return null;
		setConciliaError(null);
		setErrorPrompts(null);
		setInvalidOrders(null);
		var orders = await parseConcilia(type.type, conciliaText.map(a=>a.text), date);
		if(orders.error){
			var file = conciliaText[orders.file];
			return setConciliaError(`Hubo un error con el archivo de conciliación: ${file.filename || `(${orders.file+1})`} (LCL-CCLF${type.type}-${orders.code})`);
		}
		if(orders.orders.length==0 && orders.invalid.length==0){
			return setConciliaError('El archivo de conciliación no contiene ordenes válidas del sistema');
		}
		setInvalidOrders(orders.invalid || []);
		sendOrders(orders.orders, orders.invalid, type.type);
	}

	var onDepositDateSelected = (d: Date)=>{
		setDepositDate(d.getTime()/1000);
		parseOrders(fileType, d.getTime()/1000);
	}

	var selectType = (type: ConciliaFileType)=>{
		return async ()=>{
			setFileType(type);
			setInvalidOrders(null);
			setConciliaError(null);
			setConciliaOrders(null);
			setDepositDate(null);
			if(!type.askDate){
				parseOrders(type, null);
			}
		}
	}

	var sendOrders = (orders: ParsedOrder[], invalid_orders: InvalidParsedOrder[], type: ConciliaFile)=>{
		if((!orders || orders.length==0) && (!invalid_orders || invalid_orders.length==0)) return;
		
		var valid_orders = orders.filter(a=>a.order_id && a.payment_id);
		
		setConciliaError(null);
		API.getConcilia((valid_orders || []), (invalid_orders || []), type).then(res=>{
			if(res.error) return setConciliaError(res.message);
			if(valid_sources.length===1){
				setPaymentSource(valid_sources[0].source_id);
			}
			setPaymentMethods(res.data.payment_methods);
			setConciliaOrders([
				...res.data.deposits.map(a=>{
					var dp = valid_orders.find(b=>b.external_id===a.external_id);
					if(!dp) return a;
					return {
						...a,
						card_type: dp.card_type,
					}
				}),
				...(invalid_orders.map(a=>{
					var found = res.data.missing.find(b=>b.external_id==a.external_id);
					return {
						order_id: null,
						payment_id: null,
						amount: a.amount,
						date_deposit: a.date_deposit,
						deposit_type: a.deposit_type,
						deposit_total: a.deposit_type===DepositType.DEPOSIT ? a.amount : 0,
						deposit_refund_total: a.deposit_type!==DepositType.DEPOSIT ? a.amount : 0,
						current_deposit_refund_total: 0,
						current_deposit_total: 0,
						external_id: a.external_id,
						payment_method: a.payment_method,
						status: !!found ? ConciliaStatus.ALREADY : ConciliaStatus.MISSING,
						card_type: a.card_type,
						total: 0,
						completed: !!found,
					} as ConciliaDeposit
				}))
			]);
		}).catch(err=>{
			return setConciliaError('Hubo un error inesperado enviando la información de conciliación. (LCL-CCL-1)');
		});
	}

	var resetConcilia = ()=>{
		setFileType(null);
		setConciliaText(null);
		setInvalidOrders(null);
		setConciliaOrders(null);
		setPaymentSource(null);
	}
	
	var handleFile = async (files: File[])=>{
		var concilia_texts : typeof conciliaText = [];
		for(var i of files){
			var tx = await readFile(i);
			concilia_texts.push({
				filename: i.name,
				text: tx,
			});
		}
		setFileType(null);
		setInvalidOrders(null);
		setConciliaText(concilia_texts);
	}

	var doConcilia = (force: boolean=false)=>{
		if(!conciliaOrders || conciliaOrders.length==0) return;
		if(!force){
			var incomplete = 0;
			for(var i of conciliaOrders){
				if(i.status===ConciliaStatus.INCOMPLETE || i.status===ConciliaStatus.TOO_MUCH) incomplete++;
			}
			if(incomplete>0){
				setConciliaMode(null);
				setShownModal(ShownModal.FORCE_CONCILIA);
				return;
			}
		}
		var concilia = conciliaOrders.filter(a=>!conciliaMode || conciliaMode===0 ? (a.status===ConciliaStatus.AVAILABLE) : (a.status===ConciliaStatus.AVAILABLE || a.status===ConciliaStatus.INCOMPLETE || a.status===ConciliaStatus.TOO_MUCH));
		if(concilia.length==0){
			return Toast.error('No hay ordenes disponibles para conciliar');
		}
		if(!paymentSource){
			return Toast.error('Favor de seleccionar el origen de la conciliación')
		}
		var method = depositTypeMethod(fileType.type);
		if(!method || method.length==0) return Toast.error('El archivo ingresado no está configurado con un método de pago. (LCL-CCL-2)');
		setShownModal(null);
		setSending(true);
		setErrorPrompts(null);
		API.doConcilia(concilia, paymentSource).then(res=>{
			if(res.error) return setErrorPrompts([res.message]);
			if(!res.data || !res.data.orders || res.data.orders.length==0){
				return Toast.error('Ninguno de los depsitos pudo ser conciliado. (LCL-CCL-3)');
			}
			Toast.success('Se han conciliado las ordenes disponibles.');
			var cclo = [...conciliaOrders];
			for(var i of res.data.orders){
				var ox = cclo.findIndex(a=>a.external_id==i);
				if(ox==-1) continue;
				cclo[ox].completed = true;
			}
			setConciliaOrders(cclo);
		}).catch(err=>{
			setErrorPrompts(['Hubo un error inesperado realizando la conciliación (LCL-CCL-4)']);
		}).finally(()=>{
			setSending(false);
		})
	}

	var showSingleConcilia = (external_id: string)=>{
		return ()=>{
			var concilia = conciliaOrders.find(a=>a.external_id===external_id);
			if(!concilia) return Toast.error('No se encontró el deposito seleccionado.');
			setErrorPrompts(null);
			setSelectedExternal(external_id);
			setExternallSearch(null);
			setExternallSearchValue('');
			setModalPaymentSource(valid_sources.length==1 ? valid_sources[0].source_id : null);
			setShownModal(ShownModal.SINGLE_CONCILIA);
		}
	}
	
	var singleConcilia = (external_id: string, external?: ExternalSearchOrder)=>{
		return (setLoading: SetLoading)=>{
			var concilia = conciliaOrders.find(a=>a.external_id===external_id);
			if(!concilia) return Toast.error('No se encontró el deposito seleccionado.');

			if(valid_sources.length>0 && !modalPaymentSource){
				return setErrorPrompts(['Favor de selecciona el origen del archivo'])
			}

			setLoading(true);
			setErrorPrompts(null);
			API.doConcilia([{
				...concilia,
				order_id: external ? external.order_id : concilia.order_id,
				payment_id: external ? external.payment_id : concilia.payment_id,
			}], modalPaymentSource).then(res=>{
				if(res.error) return setErrorPrompts([res.message]);
				if(!res.data || !res.data.orders || res.data.orders.length==0){
					return Toast.error('Ninguno de los depsitos pudo ser conciliado. (LCL-CCL-6)');
				}
				Toast.success('Se ha conciliado la orden seleccionada');
				var cclo = [...conciliaOrders];
				for(var i of res.data.orders){
					var ox = cclo.findIndex(a=>a.external_id==i);
					if(ox==-1) continue;
					cclo[ox].completed = true;
					if(external){
						// cclo[ox].order_id = external.order_id;
						// cclo[ox].payment_id = external.payment_id;
						cclo[ox].total = external.total;
						cclo[ox].status = ConciliaStatus.ALREADY;
					}
				}
				setShownModal(null);
				setSelectedExternal(null);
				setConciliaOrders(cclo);
			}).catch(err=>{
				setErrorPrompts(['Hubo un error inesperado realizando la conciliación (LCL-CCL-7)']);
			}).finally(()=>{
				setLoading(false);
			})
		}
	}

	var searchPayment = (payment_id: number)=>{
		API.getPaymentConcilia(payment_id).then(res=>{
			if(res.error) Toast.error(res.message);
			setExternallSearch({
				...externalSearch,
				error: res.error,
				loading: false,
				order: res.error ? null : res.data,
			})
		}).catch(err=>{
			setExternallSearch({
				...externalSearch,
				error: true,
				loading: false,
				order: null,
			})
		})
	}

	var onExternalSearch = (val: string)=>{
		setExternallSearchValue(val);
		if(searchTimeout) clearTimeout(searchTimeout);
		if(Validator.validate([{ rule: 'number' }], val)){
			if(externalSearch && externalSearch.value.trim()==val.trim()) return;
			setExternallSearch({
				value: val,
				error: false,
				loading: true,
				order: null
			});
			setSearchTimeout(setTimeout(()=>{
				searchPayment(parseInt(val));
			}, 1000));
		}else{
			setExternallSearch(null);
		}
	}

	var { status, stats } = useMemo(()=>{
		var status = {
			deposit: 0,
			available: 0,
			already: 0,
			missing: 0,
			incomplete: 0,
			refunds: 0,
			chargeback: 0,
			completed: 0,
			excedes: 0,
			card_credit: 0,
			card_debit: 0,
			card_unknown: 0,
			invalid: invalidOrders ? invalidOrders.length : 0,
		}
		var stats = {
			total: 0,
			deposit: 0,
			available: 0,
			already: 0,
			missing: 0,
			invalid: 0,
			chargeback: 0,
			refund: 0,
			card_credit: 0,
			card_debit: 0,
			card_unknown: 0,
			payment_methods: ([] as { method_id: PaymentMethods, amount: number, deposits: number }[])
		}

		if(conciliaOrders && conciliaOrders.length>0){
			for(let i of conciliaOrders){
				if(!i.order_id || !i.payment_id){
					stats.invalid += i.amount;
				}
				if(i.completed) status.completed++;
				if(i.status==ConciliaStatus.AVAILABLE){
					status.available++;
					stats.available += i.amount;
				}
				if(i.status==ConciliaStatus.ALREADY){
					status.already++;
					stats.already += i.amount;
				}
				if(i.status==ConciliaStatus.MISSING){
					stats.missing += i.amount;
					status.missing++;
				}
				if(i.status==ConciliaStatus.INCOMPLETE) status.incomplete++;
				if(i.status==ConciliaStatus.TOO_MUCH) status.excedes++;
				if(i.deposit_type==DepositType.CHARGEBACK){
					stats.chargeback += Math.abs(i.amount);
					status.chargeback++;
					stats.total -= Math.abs(i.amount);
				}else if(i.deposit_type===DepositType.REFUND){
					stats.refund += Math.abs(i.amount);
					status.refunds++;
					stats.total -= Math.abs(i.amount);
				}else{
					status.deposit += 1;
					stats.deposit += i.amount;
					stats.total += i.amount;
				}
				if(i.card_type===CardType.CREDIT){
					status.card_credit += 1;
					stats.card_credit += i.amount;
				}else if(i.card_type===CardType.DEBIT){
					status.card_debit += 1;
					stats.card_debit += i.amount;
				}else if(i.card_type===CardType.UNKNOWN){
					status.card_unknown += 1;
					stats.card_unknown += i.amount;
				}
				var pmx = stats.payment_methods.findIndex(a=>a.method_id===i.payment_method);
				if(pmx==-1){
					stats.payment_methods.push({
						method_id: i.payment_method,
						amount: i.deposit_total * (i.deposit_type===DepositType.DEPOSIT ? 1 : -1),
						deposits: 1
					});
				}else{
					stats.payment_methods[pmx].amount += i.deposit_total * (i.deposit_type===DepositType.DEPOSIT ? 1 : -1);
					stats.payment_methods[pmx].deposits += 1;
				}
			}
		}
		return { status, stats };
	}, [conciliaOrders, invalidOrders]);

	var status_locale = useMemo(()=>{
		var locale : string[] = [];
		if(status.available>0){
			locale.push(`${status.available} conciliable${status.available==1 ? '' : 's'}`);
		}
		if(status.missing>0){
			locale.push(`${status.missing} no encontrada${status.missing==1 ? '' : 's'}`);
		}
		if(status.incomplete>0){
			locale.push(`${status.incomplete} incompleta${status.incomplete==1 ? '' : 's'}`);
		}
		if(status.excedes>0){
			locale.push(`${status.excedes} excedente${status.excedes==1 ? '' : 's'}`);
		}
		if(locale.length==0){
			locale.push('0 conciliable');
		}
		if(status.invalid>0){
			locale.push(`${invalidOrders.length} inválida${invalidOrders.length==1 ? '' : 's'}`);
		}
		return locale;
	}, [status, invalidOrders]);

	var shown_external = useMemo(()=>{
		return selectedExternal ? conciliaOrders?.find(a=>a.external_id===selectedExternal) : null;
	}, [selectedExternal, conciliaOrders]);

	var valid_sources = useMemo(()=>{
		if(!fileType || fileType.sources.length==0) return [];
		return fileType.sources.map(a=>{
			var bsn = BusinessSources.find(b=>a.business_id===b.business_id);
			return {
				source_id: a.source_id,
				business_id: a.business_id,
				business_name: bsn?.business_name
			}
		});
	}, [fileType]);

	// var selected_payment_source = useMemo(()=>{
	// 	if(!paymentSource) return null;
	// 	var src = getPaymentSource(paymentSource);
	// 	if(!src) return src;
	// 	var bsn = BusinessSources.find(a=>a.business_id===src.business_id);
	// 	src.source_name = bsn?.business_name || null;
	// 	return src;
	// }, [paymentSource]);

	// var selected_modal_payment_source = useMemo(()=>{
	// 	if(!modalPaymentSource) return null;
	// 	var src = getPaymentSource(modalPaymentSource);
	// 	if(!src) return src;
	// 	var bsn = BusinessSources.find(a=>a.business_id===src.business_id);
	// 	src.source_name = bsn?.business_name || null;
	// 	return src;
	// }, [modalPaymentSource]);

	var can_concilia = ((status.available+status.incomplete)-status.completed)>0;
	
	return <div className='ar conciliacion-screen'>
		<Groupper style={{ maxWidth: 700, margin: 'auto' }} fitted title='Conciliación' titleRight={(conciliaOrders!==null || invalidOrders!==null) && (
			<Button size='tiny' text='Reiniciar' color='blue' iconName='arrow-left' onClick={resetConcilia} />
		)}>
			{conciliaOrders===null && invalidOrders===null && (
				<Dropzone disabled={sending} done={!conciliaError && conciliaText && conciliaText.length>0 && !!fileType} style={{ maxWidth: 350, margin: 'auto', paddingTop: 20, paddingBottom: 20, }} onFiles={handleFile} valid={['.txt', '.csv']} multiple />
			)}
			{!shownModal && (
				<Message list={errorPrompts} style={{ margin: 15, marginTop: -5 }} type='error' />
			)}
			{conciliaError ? <>
				<Groupper.Divider style={{ margin: 0 }} />
				<Header text='Error' subtext={conciliaError} style={{ marginTop: 50 }} />
				<Button text='Reintentar' onClick={()=>{
					setConciliaError(null);
					setTimeout(() => {
						parseOrders(fileType, depositDate);
					}, 500);
				}} style={{ margin: 'auto', width: 150, display: 'block', marginBottom: 2 }} />
				<Button text='Cambiar tipo de archivo' size='tiny' basic onClick={()=>setFileType(null)} style={{ margin: 'auto', width: 250, display: 'block' }} />
				<div style={{ height: 50 }} />
			</> : (conciliaOrders!==null && invalidOrders!==null) ? <>
				<Table className='summary' striped details style={{ maxWidth: 400, borderLeft: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
					<Table.Body>
						<Table.Row data={['Archivo', fileType.name]} />
						{!!depositDate && (
							<Table.Row data={['Fecha deposito', moment.unix(depositDate).format('DD/MMM/YY')]} />
						)}
						<Table.Row data={['Origen', (
							(valid_sources.length>0) ? (
								<Dropdown
									value={paymentSource}
									items={valid_sources.map(a=>({
										text: a.business_name,
										value: a.source_id,
									}))}
									onValueChange={setPaymentSource}
									placeholder={'Origen'}
									type={!paymentSource ? 'error' : null}
									style={{ margin: 0 }}
								/>
							) : (
								<i style={{ color: 'gray' }}>No configurado <Icon name='exclamation-triangle' /></i>
							)
						)]} />
						{stats.payment_methods.length>0 && <Table.Divider />}
						{stats.payment_methods.map(a=>{
							var pm = paymentMethods ? paymentMethods.find(b=>b.method_id===a.method_id) : null;
							return <Table.Row data={[`${pm?.method_name_internal || `??? <${a.method_id}>`} (${a.deposits})`, addCommas(a.amount)]} />
						})}
						<Table.Divider />
						{stats.available!=0 && <Table.Row data={[`Conciliable (${status.available})`, addCommas(stats.available)]} />}
						{stats.already!=0 && <Table.Row data={[`Ya conciliado (${status.already})`, addCommas(stats.already)]} />}
						{stats.missing!=0 && <Table.Row data={[`No encontradas (${status.missing})`, addCommas(stats.missing)]} />}
						{stats.invalid!=0 && <Table.Row data={[`Invalidos (${status.invalid})`, addCommas(stats.invalid)]} />}
						{(stats.card_credit!=0 || stats.card_debit!=0) && <>
							<Table.Divider />
							{stats.card_debit!=0 && <Table.Row data={[`Débito (${status.card_debit})`, addCommas(stats.card_debit)]} />}
							{stats.card_credit!=0 && <Table.Row data={[`Crédito (${status.card_credit})`, addCommas(stats.card_credit)]} />}
							{stats.card_unknown!=0 && <Table.Row data={[`Tarj. Desconocida (${status.card_unknown})`, addCommas(stats.card_unknown)]} />}
						</>}
						<Table.Divider />
						{stats.deposit!=0 && <Table.Row data={[`Depositos (${status.deposit})`, addCommas(stats.deposit)]} />}
						{stats.refund!=0 && <Table.Row data={[`Reembolsos (${status.refunds})`, addCommas(-stats.refund)]} />}
						{stats.chargeback!=0 && <Table.Row data={[`Contracargo (${status.chargeback})`, addCommas(stats.chargeback)]} />}
						{stats.total!=0 && <Table.Row data={[`Total (${status.deposit+status.refunds+status.chargeback})`, addCommas(stats.total)]} />}
					</Table.Body>
				</Table>
				<Table striped>
					<Table.Body>
						<Table.Row header>
							<Table.Cell colSpan={5}>
								{status_locale.join(', ')}
							</Table.Cell>
							<Table.Cell style={{ textAlign: 'right' }} colSpan={2}>
								<Button size='tiny' iconName='refresh' color='blue' style={{ marginRight: 5, minWidth: 40 }} iconStyle={{ marginRight: 0 }} onClick={()=>{
									setConciliaOrders(null);
									parseOrders(fileType, depositDate);
								}} />
								<Button loading={sending} color='black' text={can_concilia ? 'Conciliar' : 'No conciliable'} disabled={!can_concilia} onClick={()=>doConcilia(false)} style={{ minWidth: 120 }} />
							</Table.Cell>
						</Table.Row>
						{status.chargeback>0 && (
							<Table.Row header>
								<Table.Cell colSpan={7} style={{ padding: 8 }}>
									<Message text='Conciliación contiene contracargos' centered type='info' />
								</Table.Cell>
							</Table.Row>
						)}
						<Table.Row header centeredIndexes={[1, 2, 4, 5, 6]} data={['ID', 'Total', 'Cantidad', 'Fecha', '%', 'Tipo', 'Status']} />
						{!conciliaOrders || conciliaOrders.length===0 ? (
							<Table.Cell row colSpan={7}>
								<Header text='Error' subtext={'No se encontraron ordenes en esta conciliación.'} />
							</Table.Cell>
						) : conciliaOrders.sort(firstBy((v: any)=>(v.status===2 || !!v.completed), 'asc').thenBy('date_deposit', 'asc').thenBy('status', 'desc').thenBy('order_id', 'desc')).map((a, i)=>{
							var percent = Math.abs(roundNumber(a.amount>0 ? (a.deposit_total*100)/a.total : (a.deposit_refund_total*100)/a.total));
							var is_less = toCents(a.amount>0 ? a.deposit_total : a.deposit_refund_total)<toCents(a.total) && a.deposit_type==DepositType.DEPOSIT;
							var is_more = toCents(a.amount>0 ? a.deposit_total : a.deposit_refund_total)>toCents(a.total);

							var show_dropdown = !(a.completed || a.status===ConciliaStatus.ALREADY) && a.status!==ConciliaStatus.AVAILABLE;
							var LabelComponent = <div className={classNames('fr label', {
								red: !a.completed && a.status===ConciliaStatus.MISSING,
								green: !a.completed && a.status===ConciliaStatus.AVAILABLE,
								yellow: !a.completed && (a.status===ConciliaStatus.INCOMPLETE || a.status===ConciliaStatus.TOO_MUCH),
							})} style={{ minWidth: 118, cursor: 'pointer' }}>
								{
									a.completed || a.status===ConciliaStatus.ALREADY ? 'Ya conciliado' : 
									a.status===ConciliaStatus.AVAILABLE ? 'Conciliable' :
									a.status===ConciliaStatus.TOO_MUCH ? 'Excedente' :
									a.status===ConciliaStatus.INCOMPLETE ? 'Incompleta' : 
									a.status===ConciliaStatus.MISSING ? 'Inválida' : '???'
								}
								{show_dropdown && (
									<Icon name='caret-down' style={{ marginRight: 0, marginLeft: 8 }} />
									// <i className="caret down icon" style={{ marginRight: 0, marginLeft: 8, width: 8, fontSize: 12 }}></i>
								)}
							</div>

							var dropdown_options : MenuItemProps[] = [];
							if(a.order_id){
								dropdown_options.push({
									key: 0,
									as: Link,
									target: '_blank',
									to: `/orders/@${a.order_id}`,
									text: 'Ver orden'
								})
								dropdown_options.push({ key: 1, text: 'Conciliar asi', onClick: showSingleConcilia(a.external_id) })
							}
							dropdown_options.push({
								key: 2,
								text: 'Conciliar otro',
								onClick: showSingleConcilia(a.external_id),
							})

							return <Table.Row key={`CCLTB-${a.external_id}-${i}`}>
								{a.order_id ? <>
									<Table.Cell collapsing>
										{a.order_id ? (
											<Link to={`/orders/@${a.order_id}`} style={{ padding: 0 }} target='_blank'>
												{a.order_id}
											</Link>
										) : (
											<Icon name='minus' color='gray' />
										)}
									</Table.Cell>
									<Table.Cell collapsing>
										{addCommas(a.total)}
									</Table.Cell>
								</> : (
									<Table.Cell centered colSpan={2} style={{ color: a.completed ? 'black' : 'brown', wordWrap: 'break-word', fontSize: a.external_id.length>15 ? 11 : 14 }}>
										<b>{a.external_id}</b>
									</Table.Cell>
								)}
								<Table.Cell collapsing>
									{addCommas(a.amount)}
								</Table.Cell>
								<Table.Cell>
									{moment.unix(a.date_deposit).format('DD/MMM/YY')}
								</Table.Cell>
								<Table.Cell collapsing centered>
									{a.status===ConciliaStatus.MISSING ? (
										<Label color='red' style={{ padding: '2px 12px' }}>
											<Icon name="remove" color="brown" style={{ marginRight: 0, fontSize: 14 }} />
										</Label>
									) : !a.order_id ? (
										null
									) : a.status===ConciliaStatus.ALREADY ? (
										<Label style={{ padding: '2px 12px' }}>
											<Icon name='check' style={{ marginRight: 0, fontSize: 14 }} />
										</Label>
									) : (
										<span style={{ textAlign: 'center', fontSize: 16, fontWeight: 'bold', color: is_less ? 'brown' : (is_more ? '#e06c00' : 'black') }}>
											{addCommas(percent, true, percent<=100)}%
										</span>
									)}
								</Table.Cell>
								<Table.Cell collapsing centered>
									{depositTypeLocale(a.deposit_type)}
								</Table.Cell>
								<Table.Cell collapsing centered>
									{show_dropdown ? (
										<Menu
											trigger={LabelComponent}
											items={dropdown_options}
										/>
									) : LabelComponent}
								</Table.Cell>
							</Table.Row>
						})}
					</Table.Body>
				</Table>
			</> : conciliaText && conciliaText.length>0 && !!fileType ? (
				fileType.askDate && !depositDate ? <>
					<Message centered type='info' style={{ maxWidth: 400, margin: 'auto', marginBottom: 15 }}>
						Selecciona la fecha de deposito del archivo <br />
						<b>{fileType.name}</b> <br />
						{conciliaText.map(a=><React.Fragment key={`cclfl-${a.filename}`}>
							{a.filename} <br />
						</React.Fragment>)}
					</Message>
					<Groupper fitted style={{ maxWidth: 400, margin: 'auto', overflow: 'hidden', marginBottom: 15 }}>
						<Calendar onSelected={onDepositDateSelected} initialDate={now.toJSDate()} mode='date' maxDate={now.toJSDate()} />
					</Groupper>
				</> : <>
					{/* <Groupper.Divider style={{ margin: 0 }} /> */}
					<Header loading text='Cargando conciliación' style={{ padding: '50px 0' }} />
				</>
			) : null}
		</Groupper>
		<Modal open={!!conciliaText && conciliaText.length>0 && !fileType} onClose={bindClick(setConciliaText, null)} size='mini'>
			<Modal.Header style={{ borderBottomWidth: 0 }}>Tipo de archivo</Modal.Header>
			<Input ref={typeSearchRef} value={typeSearch} onChange={setTypeSearch} placeholder='Buscar tipos de conciliación' inputStyle={{ borderRadius: 0, padding: 15 }} style={{ marginBottom: 0 }} />
			<Modal.Content style={{ minHeight: 200, padding: 0 }}>
				<div className="fr striped divided selectable items">
					{FileTypeList.map(a=>(
						<div className="item" onClick={selectType(a)} key={`FTL-${a.type}`}>
							<Icon name={a.iconName || 'file-text'} brand={!!a.iconBrand} />
							{a.name}
						</div>
					))}
				</div>
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' onClick={bindClick(setConciliaText, null)} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal===ShownModal.FORCE_CONCILIA && conciliaOrders && conciliaOrders.length>0} onClose={bindClick(setShownModal)} size='mini'>
			<Modal.Header>Conciliar Ordenes</Modal.Header>
			<Modal.Content>
				<Header text='Conciliación' subtext='Hay ordenes que no tienen su conciliación completa, favor de decidir que hacer con ellas.' />
				<Table style={{ marginTop: 15 }}>
					<Table.Body>
						{[
							{ id: 0, text: `Conciliar solo completas`, meta: `${status.available} completa${status.available==1 ? '' : 's'}` },
							{ id: 1, text: `Conciliar todas`, meta: [
								status.available>0 ? `${status.available} completa${status.available==1 ? '' : 's'}` : null, 
								status.incomplete>0 ? `${status.incomplete} incompleta${status.incomplete==1 ? '' : 's'}` : null,
								status.excedes>0 ? `${status.excedes} excediente${status.excedes==1 ? '' : 's'}` : null,
							].filter(a=>!!a).join(' + ') },
						].map(a=>{
							var selected = conciliaMode===a.id;
							return <Table.Row key={`CMD-${a.id}`} selectable onClick={bindClick(setConciliaMode, a.id)}>
								<Table.Cell style={{ paddingRight: 0 }} collapsing value={<Icon name={selected ? 'circle-check' : 'circle'} fitted size={18} solid={selected} />} />
								<Table.Cell>
									<div style={{ fontWeight: 'bold' }}>{a.text}</div>
									<div style={{ color: 'gray', fontSize: 12, lineHeight: '12px' }}>{a.meta}</div>
								</Table.Cell>
							</Table.Row>
						})}
					</Table.Body>
				</Table>
				<div style={{ height: 5, width: 1 }} />
				{conciliaMode==1 && status.excedes>0 && (
					<Message size='small' type='warning' centered style={{ marginTop: 10 }}>
						Las ordenes excedientes o incompletas serán conciliadas con la cantidad depositada
					</Message>
				)}
			</Modal.Content>
			<Modal.Actions>
				<Button basic text='Cancelar' onClick={bindClick(setShownModal)} />
				<Button color='black' onClick={()=>doConcilia(true)} text='Conciliar' disabled={conciliaMode===null} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal===ShownModal.SINGLE_CONCILIA && !!shown_external} onClose={bindClick(setShownModal)} size='tiny'>
			<Modal.Header>Conciliar deposito</Modal.Header>
			{shownModal===ShownModal.SINGLE_CONCILIA && !!shown_external && <Modal.Content>
				<Header text='Conciliar deposito' subtext='Esto conciliará el deposito seleccionado' />
				<Table details style={{ marginBottom: 15 }}>
					<Table.Head titleSize='small' title='Información' />
					<Table.Body data={[
						['Orden', externalSearch?.order?.order_id || shown_external.order_id || <i>No encontrada</i>],
						['Tipo', depositTypeLocale(shown_external.deposit_type)],
						['Referencia', shown_external.external_id],
						['Depositado', addCommas(shown_external.amount)],
						// null,
						['Total pagado', (
							externalSearch?.order ? (
								addCommas(externalSearch.order.total)
							) : shown_external.order_id ? (
								addCommas(shown_external.total)
							) : (
								<Icon name='minus' color='gray' />
							)
						)],
						['Total conciliado', (
							externalSearch?.order ? (
								addCommas(shown_external.amount+externalSearch.order.current_deposit_total)
							) : shown_external.order_id ? (
								addCommas(shown_external.amount+shown_external.current_deposit_total)
							) : (
								<Icon name='minus' color='gray' />
							)
						)],
						['Avance', (
							externalSearch?.order ? (
								addCommas(Math.floor(((shown_external.amount+(shown_external.amount>0 ? externalSearch.order.current_deposit_total : externalSearch.order.current_deposit_refund_total))*100)/externalSearch.order.total), false)+'%'
							) : shown_external.order_id ? (
								addCommas(Math.floor(((shown_external.amount+(shown_external.amount>0 ? shown_external.current_deposit_total : shown_external.current_deposit_refund_total))*100)/shown_external.total), false)+'%'
							) : (
								<Icon name='minus' color='gray' />
							)
						)],
					]} />
				</Table>
				{!shown_external.order_id && (
					<Field label='Pago' comment='ID del pago, NO de la orden' style={{ marginTop: 10 }}>
						<Input placeholder='Pago' value={externalSearchValue} onChange={onExternalSearch} buttons={(
							<Button icon iconName={externalSearch ? (!externalSearch.loading ? (externalSearch.error ? 'triangle-exclamation' : !externalSearch.order ? 'times-circle' : 'check-circle') : 'search') : 'search'} loading={!!externalSearch?.loading} />
							// <div className='label'>
							// 	{externalSearch ? (
							// 		externalSearch.loading ? (
							// 			<div className="fr loading inline"></div>
							// 		) : (
							// 			<Icon name={externalSearch.error ? 'triangle-exclamation' : !externalSearch.order ? 'times-circle' : 'check-circle'} />
							// 		)
							// 	) : (
							// 		<Icon name='search' style={{ fontSize: 16, lineHeight: '14px' }} />
							// 	)}
							// </div>
						)} />
					</Field>
				)}
				<Dropdown label='Origen' value={modalPaymentSource} items={valid_sources ? valid_sources.map(a=>({ text: a.business_name, value: a.source_id })) : []} onValueChange={(v, d)=>{
					var ds = valid_sources.find(a=>a.source_id===d.value);
					setModalPaymentSource(ds.source_id);
				}} />
				{!!shown_external.order_id || !!externalSearch?.order && (
					<Message centered style={{ marginTop: 15 }}>
						Se conciliará <b>{addCommas(shown_external.amount)}</b> a la orden <b>{shown_external.order_id || externalSearch.order?.order_id}</b> con la referencia <b>{shown_external.external_id}</b>
					</Message>
				)}
				<Message list={errorPrompts} type='error' />
			</Modal.Content>}
			<Modal.Actions>
				<Button text='Cancelar' basic onClick={bindClick(setShownModal)} />
				<Button text='Conciliar' disabled={!shown_external?.order_id && !externalSearch?.order} color='black' onClick={singleConcilia(selectedExternal, externalSearch?.order)} />
			</Modal.Actions>
		</Modal>
	</div>
}

export default Conciliacion;